/**
 * =============================================================================
 * HOME PAGE - Início
 * =============================================================================
 *
 * Página inicial do site Oficina da Mente. Contém:
 * 1. Hero: Vídeo de fundo + título animado + botões CTA
 * 2. Welcome: Foto do Pedro + citação filosófica
 * 3. Services: Cards de serviços
 * 4. Space: Galeria de fotos do espaço físico (com lightbox)
 * 5. Contact Snippet: Telefone, email e morada
 */

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, GraduationCap } from "lucide-react";
import { useState } from "react";
import { Seo } from "@/components/seo/Seo";
import { useSeo } from "@/hooks/useSeo";
import { Section } from "@/components/Section";
import { SectionHeader } from "@/components/SectionHeader";
import { ServiceCard } from "@/components/ServiceCard";
import { ContactCard } from "@/components/ContactCard";
import { ImageWithFallback } from "@/components/ImageWithFallback";
import ImageModal from "@/components/ImageModal";
import { services } from "@/data/services";
import { contactItems } from "@/data/contact";
import { spacePhotos } from "@/data/space";
import { ROUTES } from "@/constants/routes";

export default function Home() {
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  return (
    <div>
      <Seo {...useSeo()} />

      {/* Hero */}
      <section className="relative w-full aspect-[16/9] flex items-center justify-center overflow-hidden">
        <img
          src="/images/mar-inicio.gif"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto py-20">
          <div className="bg-deepOcean/60 backdrop-blur-sm p-8 sm:p-12 rounded-2xl shadow-2xl">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-playfair text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-pureWhite mb-4"
            >
              PEDRO <span className="font-normal italic">de</span> FREITAS
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg sm:text-xl md:text-2xl text-softSky tracking-[0.2em] uppercase font-medium mb-8"
            >
              Oficina da Mente
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-base sm:text-lg text-pureWhite/80 max-w-2xl mx-auto mb-10 leading-relaxed"
            >
              Hipnoterapia, formação e ferramentas para o bem-estar mental.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to={ROUTES.hipnoterapia} className="btn-primary">
                <Brain size={18} aria-hidden="true" />
                Hipnoterapia
              </Link>
              <Link
                to={ROUTES.formacao}
                className="btn-secondary border-pureWhite text-pureWhite hover:bg-pureWhite hover:text-deepOcean"
              >
                <GraduationCap size={18} aria-hidden="true" />
                Formação
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Welcome */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="relative flex justify-center"
          >
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-oceanBlue/20 animate-breathe" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-rose/20 animate-breathe animate-delay-200" />

              <div className="relative w-64 h-80 sm:w-72 sm:h-96 rounded-2xl overflow-hidden shadow-xl bg-deepOcean/10">
                <ImageWithFallback
                  src="images/pedro-de-freitas-1.jpg"
                  alt="Pedro de Freitas - Oficina da Mente"
                  className="w-full h-full object-cover"
                  containerClassName="w-full h-full"
                />
                <span
                  aria-hidden="true"
                  className="absolute bottom-2 right-3 text-xs italic text-pureWhite/70 drop-shadow-md select-none"
                >
                  Tomar 2011
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <p className="text-sm uppercase tracking-[0.2em] text-oceanBlue font-medium mb-3">
              Bem-vindo à Oficina da Mente
            </p>

            <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-deepOcean mb-6">
              A mudança começa dentro de nós
            </h2>

            <blockquote className="border-l-4 border-oceanBlue pl-4 italic text-textMuted mb-6">
              Não esqueçamos que quando apontamos o dedo a alguém, temos logo 3 virados para nós.
            </blockquote>

            <p className="text-textBody/80 leading-relaxed mb-4">
              Antes de julgar ou fazer um juízo de valor, lembremo-nos que existe todo um contexto (ou antecedentes) que nos levariam, talvez, a fazer o mesmo.
            </p>
            <p className="text-textBody/80 leading-relaxed">
              Quando suspendemos o julgamento e escutamos a “sabedoria interior”, descobrimos que a verdadeira mudança começa dentro de nós — e a hipnoterapia é a ponte para esse reencontro.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Services */}
      <Section alt>
        <SectionHeader
          eyebrow="Os nossos serviços"
          title="Permita-se melhorar a sua qualidade de vida"
          align="center"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={service.title} service={service} index={index} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mt-14"
        >
          <p className="font-playfair text-xl sm:text-2xl md:text-3xl text-deepOcean italic leading-relaxed">
            “Faz aos outros aquilo que gostarias que fizessem por ti.”
          </p>
        </motion.div>
      </Section>

      {/* Space Gallery */}
      <Section>
        <SectionHeader
          eyebrow="O nosso espaço"
          title="Um ambiente pensado para o seu bem-estar"
          description=""
          align="center"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {spacePhotos.map((photo, index) => (
            <motion.button
              key={photo.src}
              type="button"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onClick={() => setSelectedPhoto(photo.src)}
              className="group relative aspect-[4/3] rounded-2xl overflow-hidden shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-oceanBlue"
              aria-label={`Ampliar fotografia: ${photo.alt}`}
            >
              <ImageWithFallback
                src={photo.src}
                alt={photo.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                containerClassName="w-full h-full"
              />
              {(index === 0 || index === 1 || index === 2) && (
                <span
                  aria-hidden="true"
                  className="absolute bottom-2 right-3 text-xs italic text-pureWhite/90 drop-shadow-md select-none z-10"
                >
                  {index === 2 ? "Foto: Ema Morais" : "2026"}
                </span>
              )}
              <div className="absolute inset-0 bg-deepOcean/0 transition-colors duration-300 group-hover:bg-deepOcean/20" />
            </motion.button>
          ))}
        </div>
      </Section>

      {/* Contact Snippet */}
      <Section className="bg-deepOcean">
        <SectionHeader
          eyebrow="Contacto"
          title="Amigo(a), cuidado com o que desejas: um dia podes consegui-lo."
          align="center"
          className="[&_h2]:text-pureWhite [&_h2]:text-lg [&_h2]:sm:text-xl [&_h2]:lg:text-2xl [&_p]:text-pureWhite/70"
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {contactItems.map((item) => (
            <ContactCard key={item.label} item={item} variant="dark" />
          ))}
        </div>
      </Section>

      <ImageModal image={selectedPhoto} onClose={() => setSelectedPhoto(null)} alt="Fotografia do espaço ampliada" />
    </div>
  );
}
